const COVERAGE_PRODUCTION_BASE_URL = "https://maps.vodafone.com.au";
const COVERAGE_TEST_BASE_URL = "https://mapt.vodafone.com.au";
const DEFAULT_COVERAGE_PATH = "felix";
const DEFAULT_NETWORK_STATUS_PATH = "network-status/felix";
const DEFAULT_SCROLL_GAP = 40;

const normalizeBaseUrl = (url) => {
	if (!url || typeof url !== "string") {
		return "";
	}

	return url.trim().replace(/\/+$/, "");
};

const normalizePath = (path, fallbackPath) => {
	if (!path || typeof path !== "string") {
		return fallbackPath;
	}

	const normalizedPath = path.trim().replace(/^\/+|\/+$/g, "");
	return normalizedPath || fallbackPath;
};

const getNumericValue = (value, fallbackValue) => {
	const parsedValue = Number(value);
	return Number.isFinite(parsedValue) ? parsedValue : fallbackValue;
};

export const getCoverageBaseUrl = () => {
	return (
		normalizeBaseUrl(process.env.NEXT_PUBLIC_COVERAGE_BASE_URL) ||
		COVERAGE_PRODUCTION_BASE_URL
	);
};

export const getCoveragePath = () => {
	return normalizePath(
		process.env.NEXT_PUBLIC_COVERAGE_PATH,
		DEFAULT_COVERAGE_PATH,
	);
};

export const getNetworkStatusPath = () => {
	return normalizePath(
		process.env.NEXT_PUBLIC_NETWORK_STATUS_PATH,
		DEFAULT_NETWORK_STATUS_PATH,
	);
};

export const getCoverageScrollGap = () => {
	return getNumericValue(
		process.env.NEXT_PUBLIC_COVERAGE_SCROLL_GAP,
		DEFAULT_SCROLL_GAP,
	);
};

export const getCoverageIframeSrc = () => {
	return `${getCoverageBaseUrl()}/coverage/${getCoveragePath()}`;
};

export const getNetworkStatusIframeSrc = () => {
	return `${getCoverageBaseUrl()}/coverage/${getNetworkStatusPath()}`;
};

export const getTrustedCoverageOrigins = () => {
	return Array.from(
		new Set([
			COVERAGE_PRODUCTION_BASE_URL,
			COVERAGE_TEST_BASE_URL,
			getCoverageBaseUrl(),
		]),
	);
};

export const isTrustedCoverageOrigin = (origin) => {
	return getTrustedCoverageOrigins().includes(origin);
};

const getIframeElement = (iframeId) => {
	const iframe = document.getElementById(iframeId);
	if (!iframe) {
		return null;
	}

	return iframe;
};

const doesEventMatchIframe = (iframeId, eventSource) => {
	const iframe = getIframeElement(iframeId);
	if (!iframe) {
		return false;
	}

	if (!eventSource || !iframe.contentWindow) {
		return true;
	}

	return iframe.contentWindow === eventSource;
};

export const scrollToIframeOffset = (iframeId, offset, gap = 0) => {
	const iframe = getIframeElement(iframeId);
	if (!iframe) {
		return false;
	}

	const iframeRect = iframe.getBoundingClientRect();
	const iframePositionTop = iframeRect.top + window.parent.scrollY;
	const y = iframePositionTop + getNumericValue(offset, 0) - gap;

	window.parent.scrollTo({
		top: y,
		behavior: "smooth",
	});

	return true;
};

export const centerIframeInView = (iframeId) => {
	const iframe = getIframeElement(iframeId);
	if (!iframe) {
		return false;
	}

	const iframeRect = iframe.getBoundingClientRect();
	const iframePositionTop = iframeRect.top + window.parent.scrollY;
	const center =
		iframePositionTop - window.parent.innerHeight / 2 + iframeRect.height / 2;

	window.parent.scrollTo({
		top: center,
		behavior: "smooth",
	});

	return true;
};

export const resizeIframe = (iframeId, height, applyInlineHeight = false) => {
	const iframe = getIframeElement(iframeId);
	if (!iframe) {
		return false;
	}

	const normalizedHeight = String(getNumericValue(height, 0));
	iframe.setAttribute("height", normalizedHeight);
	iframe.setAttribute("scrolling", "no");

	if (applyInlineHeight) {
		iframe.setAttribute("style", `height:${normalizedHeight}px;`);
	}

	return true;
};

const isTrustedIframeEvent = (event, iframeId) => {
	if (!event || !isTrustedCoverageOrigin(event.origin)) {
		return false;
	}

	return doesEventMatchIframe(iframeId, event.source);
};

export const handleCoverageIframeMessage = (event) => {
	if (!isTrustedIframeEvent(event, "coverage-iframe")) {
		return false;
	}

	switch (event.data?.type) {
		case "see-more-details-event":
		case "coverage-loaded-event":
		case "scroll-to-map-info-event":
			return scrollToIframeOffset(
				"coverage-iframe",
				event.data?.offset,
				getCoverageScrollGap(),
			);
		case "use-location-event":
			return centerIframeInView("coverage-iframe");
		case "resize-iframe-event":
		case "resizecoviframe":
			return resizeIframe("coverage-iframe", event.data?.height);
		default:
			return false;
	}
};

export const handleOutageIframeMessage = (event) => {
	if (!isTrustedIframeEvent(event, "network-status-iframe")) {
		return false;
	}

	switch (event.data?.type) {
		case "resize-iframe-event":
		case "resizecoviframe":
			return resizeIframe("network-status-iframe", event.data?.height, true);
		case "outages-loaded-event":
			setTimeout(() => {
				scrollToIframeOffset("network-status-iframe", event.data?.offset, 0);
			}, 500);
			return true;
		case "use-location-event":
			return centerIframeInView("network-status-iframe");
		default:
			return false;
	}
};
